<script setup lang="ts">
import AmbassadorFlare from '@/components/common/AmbassadorFlare.vue'
import { useRoute, useRouter } from 'vue-router'
import EmptyState from '@/components/common/EmptyState.vue'
import ListingImage from '@/components/listings/ListingImage.vue'
import BaseBadge from '@/components/common/BaseBadge.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import PageLayout from '@/components/layout/PageLayout.vue'
import PageHeader from '@/components/layout/PageHeader.vue'
import { Drawer, DrawerContent } from '@/components/ui/drawer'
import { onMounted, ref, computed, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useListingsStore } from '@/stores/listings'
import { UIAvatar, UIChip } from '@/components/ui'
import {PhCaretRight, PhX, PhSlidersHorizontal, PhCaretDown, PhTag, PhCurrencyDollar, PhTrendUp, PhHash, PhPi, PhStar, PhMagnifyingGlass} from '@phosphor-icons/vue'
import AppTabbar from '@/components/common/AppTabbar.vue'
import { useScrollReveal, useTypewriterPlaceholder } from '@/composables'
import type { Category } from '@/types/api'

const router = useRouter()
const route = useRoute()
const listingsStore = useListingsStore()
const authStore = useAuthStore()

const { observe } = useScrollReveal({ threshold: 0.1 })

const searchQuery = ref('')
const selectedCategory = ref('')
const sortBy = ref('newest')
const showFilters = ref(false)

// Example queries typed into the empty search field, to show what can be found here.
const SEARCH_EXAMPLES = ['Java developer', 'Social Media Analyst', 'Logo designer', 'Wedding photographer', 'Podcast editor'] as const
const searchPlaceholder = useTypewriterPlaceholder(SEARCH_EXAMPLES, {
  prefix: 'Search for ',
  paused: computed(() => searchQuery.value.length > 0),
})

const categories = computed(() => listingsStore.categories.filter(c => !c.parentID))
/** The top-level category being browsed, also while one of its subcategories is picked. */
const activeCategory = computed(() =>
  categories.value.find(c => c.slug === selectedCategory.value || c.children?.some(sub => sub.slug === selectedCategory.value)),
)
/**
 * The subcategories Browse offers: those with listings, plus the one being viewed
 * so a link to an empty one still shows it. Main categories always show. A count
 * the backend didn't send counts as "has listings".
 */
function browsableSubcategories(category?: Category): Category[] {
  return (category?.children ?? []).filter(sub => sub.listingCount !== 0 || sub.slug === selectedCategory.value)
}
const subcategories = computed(() => browsableSubcategories(activeCategory.value))
const selectedCategoryName = computed(() => {
  const parent = activeCategory.value
  const sub = subcategories.value.find(c => c.slug === selectedCategory.value)
  return sub ? `${parent?.name} › ${sub.name}` : parent?.name
})

const sortOptions = [
  { value: 'newest', label: 'Newest', icon: PhHash },
  { value: 'price_asc', label: 'Price: Low to High', icon: PhCurrencyDollar },
  { value: 'price_desc', label: 'Price: High to Low', icon: PhCurrencyDollar },
  { value: 'popular', label: 'Most Popular', icon: PhTrendUp },
]

async function fetchListings(append = false) {
  listingsStore.setFilters({
    search: searchQuery.value || undefined,
    category: selectedCategory.value || undefined,
    sort: sortBy.value,
  })

  await listingsStore.fetchListings(append)
}

function commitFilters() {
  const query = new URLSearchParams()
  if (searchQuery.value.trim()) query.set('search', searchQuery.value.trim())
  if (selectedCategory.value) query.set('category', selectedCategory.value)
  if (sortBy.value !== 'newest') query.set('sort', sortBy.value)
  const url = `/browse/${query.size ? `?${query}` : ''}`
  // A changed URL refetches through the route.query watcher below.
  if (route.fullPath !== url) void router.replace(url)
  else void fetchListings()
}

function handleSearch() { commitFilters() }
function handleCategoryChange(category: string) {
  selectedCategory.value = category
  commitFilters()
}
function handleSortChange() { commitFilters() }
function clearFilters() {
  searchQuery.value = ''
  selectedCategory.value = ''
  sortBy.value = 'newest'
  commitFilters()
}

function loadMore() {
  if (listingsStore.hasMore) {
    fetchListings(true)
  }
}

function formatPrice(price: number) {
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function viewStorefront(username: string) {
  router.push(`/u/${encodeURIComponent(username)}/`)
}

function queryString(value: unknown) {
  return typeof value === 'string' ? value : ''
}

const browsePath = route.path
function restoreFilters() {
  // The shared route also changes while navigating away from this page.
  if (route.path !== browsePath) return
  searchQuery.value = queryString(route.query.search)
  selectedCategory.value = queryString(route.query.category)
  const requestedSort = queryString(route.query.sort)
  sortBy.value = sortOptions.some(option => option.value === requestedSort) ? requestedSort : 'newest'
  void fetchListings()
}

onMounted(() => { void listingsStore.fetchCategories() })
watch(() => route.query, restoreFilters, { immediate: true })
</script>

<template>
  <PageLayout nav="browse">
    <template #navbar>
      <PageHeader title="Browse Services" />
    </template>

    <!-- Phones: one stretched column. md+: sticky filter sidebar + results. -->
    <div class="mx-auto w-full max-w-7xl md:flex md:items-start md:gap-8 md:px-6 md:pt-8 lg:px-8">
      <aside class="sticky top-6 hidden w-64 shrink-0 space-y-6 md:block" aria-label="Filters">
        <div>
          <h2 class="mb-2 text-xs font-medium uppercase tracking-wider text-ink-5">Category</h2>
          <div class="flex flex-col gap-0.5" role="tablist" aria-label="Categories" aria-orientation="vertical">
            <template v-for="cat in [{ id: 'all', name: 'All', slug: '' }, ...categories]" :key="cat.id">
              <button
                type="button"
                role="tab"
                class="cursor-pointer rounded-lg px-3 py-2 text-left text-sm transition-colors"
                :class="selectedCategory === cat.slug ? 'bg-surface-2 font-semibold text-accent-cyan' : 'text-ink-4 hover:bg-surface hover:text-ink'"
                :aria-selected="selectedCategory === cat.slug"
                @click="handleCategoryChange(cat.slug)"
              >
                {{ cat.name }}
              </button>
              <button
                v-for="sub in cat.id === activeCategory?.id ? subcategories : []"
                :key="sub.id"
                type="button"
                role="tab"
                class="cursor-pointer rounded-lg py-1.5 pl-6 pr-3 text-left text-sm transition-colors"
                :class="selectedCategory === sub.slug ? 'bg-surface-2 font-semibold text-accent-cyan' : 'text-ink-5 hover:bg-surface hover:text-ink'"
                :aria-selected="selectedCategory === sub.slug"
                @click="handleCategoryChange(sub.slug)"
              >
                {{ sub.name }}
              </button>
            </template>
          </div>
        </div>

        <div>
          <label for="browse-sort" class="mb-2 block text-xs font-medium uppercase tracking-wider text-ink-5">
            Sort by
          </label>
          <select
            id="browse-sort"
            v-model="sortBy"
            class="w-full appearance-none rounded-xl border border-line-2 bg-surface-2 px-4 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
            @change="handleSortChange"
          >
            <option v-for="opt in sortOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>

        <BaseButton
          v-if="searchQuery || selectedCategory || sortBy !== 'newest'"
          variant="secondary"
          size="sm"
          class="w-full"
          @click="clearFilters"
        >
          Clear all
          <PhX class="ml-1 h-3 w-3" />
        </BaseButton>
      </aside>

      <div class="min-w-0 flex-1">

    <!-- Search bar (scrolls with content — BROWSE-01) -->
    <div class="px-4 pt-4 pb-2 md:px-0 md:pt-0 md:pb-4">
      <div class="relative flex items-center">
        <PhMagnifyingGlass class="absolute left-3.5 w-4 h-4 text-ink-6 pointer-events-none z-10" />
        <input
          v-model="searchQuery"
          type="search"
          :placeholder="searchPlaceholder"
          aria-label="Search services"
          class="browse-search-input w-full appearance-none bg-surface border border-line rounded-2xl py-2.5 pl-10 pr-10 text-sm leading-5 text-ink placeholder-ink-6 focus:outline-none focus:ring-2 focus:ring-cyan-400/60 focus:border-cyan-400 transition-colors"
          @keyup.enter="handleSearch"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="absolute right-3.5 text-ink-6 hover:text-ink transition-colors z-10"
          aria-label="Clear search"
          @click="() => { searchQuery = ''; handleSearch() }"
        >
          <PhX class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Mobile Filter Button -->
    <div class="md:hidden px-4 pb-4">
      <BaseButton
        variant="primary"
        class="w-full"
        @click="showFilters = true"
      >
        <PhSlidersHorizontal class="w-5 h-5 mr-2" />
        Filters
        <BaseBadge v-if="searchQuery || selectedCategory" color="cyan" class="ml-2">
          {{ (searchQuery ? 1 : 0) + (selectedCategory ? 1 : 0) }}
        </BaseBadge>
      </BaseButton>
    </div>

    <!-- Category Chips -->
    <div class="relative pb-4 md:hidden" v-observe="observe">
      <div
        class="flex gap-2 overflow-x-auto px-4 pb-2 pr-12 scrollbar-hide"
        role="tablist"
        aria-label="Categories"
      >
        <UIChip
          label="All"
          :variant="!selectedCategory ? 'accent' : 'outline'"
          size="small"
          class="flex-none whitespace-nowrap px-4 cursor-pointer"
          @click="handleCategoryChange('')"
          clickable
          role="tab"
          :aria-selected="!selectedCategory"
        />
        <UIChip
          v-for="cat in categories"
          :key="cat.id"
          :label="cat.name"
          :variant="activeCategory?.id === cat.id ? 'accent' : 'outline'"
          size="small"
          class="flex-none whitespace-nowrap px-4 cursor-pointer"
          @click="handleCategoryChange(cat.slug)"
          clickable
          role="tab"
          :aria-selected="activeCategory?.id === cat.id"
        />
      </div>
      <div
        v-if="subcategories.length"
        class="flex gap-2 overflow-x-auto px-4 pb-2 pr-12 scrollbar-hide"
        role="tablist"
        :aria-label="`${activeCategory?.name} subcategories`"
      >
        <UIChip
          :label="`All ${activeCategory?.name}`"
          :variant="selectedCategory === activeCategory?.slug ? 'accent' : 'outline'"
          size="small"
          class="flex-none whitespace-nowrap px-4 cursor-pointer"
          @click="handleCategoryChange(activeCategory?.slug ?? '')"
          clickable
          role="tab"
          :aria-selected="selectedCategory === activeCategory?.slug"
        />
        <UIChip
          v-for="sub in subcategories"
          :key="sub.id"
          :label="sub.name"
          :variant="selectedCategory === sub.slug ? 'accent' : 'outline'"
          size="small"
          class="flex-none whitespace-nowrap px-4 cursor-pointer"
          @click="handleCategoryChange(sub.slug)"
          clickable
          role="tab"
          :aria-selected="selectedCategory === sub.slug"
        />
      </div>
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-y-0 right-0 w-12 bg-linear-to-l from-(--app-page-bg) to-transparent"
      />
    </div>

    <!-- Active Filters Bar (Desktop) -->
    <div class="hidden md:block mb-4" v-if="searchQuery || selectedCategory" v-observe="observe">
      <div class="bg-surface/50 border border-line rounded-xl p-3">
        <div class="flex flex-wrap items-center gap-2">
          <BaseBadge
            v-if="searchQuery"
            color="cyan"
            removable
            @remove="() => { searchQuery = ''; handleSearch() }"
          >
            <PhHash class="w-3 h-3 mr-1" />
            Search: "{{ searchQuery }}"
          </BaseBadge>
          <BaseBadge
            v-if="selectedCategory"
            color="emerald"
            removable
            @remove="() => { selectedCategory = ''; handleCategoryChange('') }"
          >
            <PhTag class="w-3 h-3 mr-1" />
            Category: {{ selectedCategoryName }}
          </BaseBadge>
          <BaseButton
            variant="secondary"
            size="sm"
            @click="clearFilters"
          >
            Clear all
            <PhX class="w-3 h-3 ml-1" />
          </BaseButton>
        </div>
      </div>
    </div>

    <!-- Results Count -->
    <div class="px-4 mb-4 md:px-0" v-observe="observe">
      <p class="text-sm text-ink-5">
        {{ listingsStore.totalItems }} service{{ listingsStore.totalItems !== 1 ? 's' : '' }} found
      </p>
    </div>

    <!-- Listings Grid -->
    <div
      v-if="listingsStore.isLoading && listingsStore.listings.length === 0"
      class="px-4 md:px-0 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
    >
      <div v-for="i in 6" :key="i" class="aspect-video bg-surface-2 rounded-2xl animate-pulse" />
    </div>

    <EmptyState
      v-else-if="listingsStore.error"
      variant="error"
      title="Could not load services"
      :description="listingsStore.error"
      @action="fetchListings()"
    />
    <EmptyState
      v-else-if="!listingsStore.isLoading && listingsStore.listings.length === 0"
      title="No services found"
      description="Try adjusting your filters or search terms"
      action-label="Clear Filters"
      @action="clearFilters"
    />

    <div v-else class="px-4 md:px-0 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 pb-4">
      <div
        v-for="item in listingsStore.listings"
        :key="item.id"
        class="bg-transparent"
        @click="() => router.push(`/listing/${item.id}/`)"
      >
        <div class="group relative bg-surface border border-line rounded-2xl overflow-hidden h-full flex flex-col transition-all hover:border-line-2 hover:shadow-[0_20px_40px_rgb(0,0,0/0.3)] cursor-pointer">
          <ListingImage :src="item.images[0]" :alt="item.title" zoom>
            <BaseBadge
              v-if="item.status !== 'active'"
              :color="item.status === 'paused' ? 'amber' : 'slate'"
              class="absolute top-3 right-3 text-xs"
            >
              {{ item.status }}
            </BaseBadge>

            <div class="absolute bottom-3 left-3 bg-black/80 backdrop-blur px-3 py-1.5 rounded-xl flex items-center gap-2">
              <PhPi class="w-5 h-5 text-amber-400" />
              <span class="font-bold text-white">{{ formatPrice(item.pricePi) }}</span>
            </div>
          </ListingImage>

          <div class="p-4 flex-1 flex flex-col">
            <div class="flex items-center gap-2 mb-2">
              <UIChip :label="item.category.name" variant="outline" size="small" />
              <span class="text-xs text-ink-6">{{ item.deliveryDays }}d delivery</span>
            </div>

            <h3 class="font-semibold text-ink mb-1 line-clamp-2 group-hover:text-accent-cyan transition-colors">
              {{ item.title }}
            </h3>

            <button
              type="button"
              class="mt-auto flex w-full items-center gap-2 border-t border-line pt-3 text-left"
              :aria-label="`View ${item.seller.username}'s storefront`"
              @click.stop="viewStorefront(item.seller.username)"
            >
              <UIAvatar
                :src="item.seller.avatarURL"
                :fallback="item.seller.username"
                size="sm"
              />
              <div class="flex-1 min-w-0">
                <p class="flex items-center gap-1 text-sm font-medium text-ink">
                  <span class="truncate">{{ item.seller.username }}</span>
                  <AmbassadorFlare v-if="item.seller.isAmbassador" />
                </p>
                <div class="flex items-center gap-1">
                  <PhStar weight="fill" class="w-3 h-3 text-accent-amber" />
                  <span class="text-xs text-ink-5">{{ item.seller.ratingAvg.toFixed(1) }}</span>
                  <span class="text-xs text-ink-6">({{ item.seller.ratingCount }})</span>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Load More -->
    <div v-if="listingsStore.hasMore" class="px-4 md:px-0 pb-8 text-center" v-observe="observe">
      <BaseButton
        variant="secondary"
        :loading="listingsStore.isLoading"
        :disabled="listingsStore.isLoading"
        class="min-w-[240px]"
        @click="loadMore"
      >
        Load More ({{ listingsStore.listings.length }} / {{ listingsStore.totalItems }})
        <PhCaretDown class="w-5 h-5 ml-2" />
      </BaseButton>
    </div>

      </div>
    </div>

    <!-- Filters Sheet -->
    <Drawer v-model:open="showFilters" swipe-direction="down">
      <DrawerContent>
        <div class="p-4 space-y-6">
          <div>
            <label class="block text-sm font-medium text-ink-4 mb-2">Search</label>
            <input
              v-model="searchQuery"
              type="search"
              :placeholder="searchPlaceholder"
              aria-label="Search services"
              class="w-full bg-surface-2 border border-line-2 rounded-xl px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
              @keyup.enter="() => { handleSearch(); showFilters = false }"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-ink-4 mb-2">Category</label>
            <select
              v-model="selectedCategory"
              aria-label="Category"
              @change="() => { handleCategoryChange(selectedCategory); showFilters = false }"
              class="w-full bg-surface-2 border border-line-2 rounded-xl px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent appearance-none"
            >
              <option value="">All Categories</option>
              <template v-for="cat in categories" :key="cat.id">
                <option :value="cat.slug">{{ cat.name }}</option>
                <option v-for="sub in browsableSubcategories(cat)" :key="sub.id" :value="sub.slug">
                  {{ cat.name }} › {{ sub.name }}
                </option>
              </template>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-ink-4 mb-2">Sort By</label>
            <select
              v-model="sortBy"
              @change="() => { handleSortChange(); showFilters = false }"
              class="w-full bg-surface-2 border border-line-2 rounded-xl px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent appearance-none"
            >
              <option v-for="opt in sortOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>

          <div class="flex gap-3 pt-4 border-t border-line">
            <BaseButton variant="secondary" class="flex-1" @click="clearFilters">
              Clear All
            </BaseButton>
            <BaseButton variant="primary" class="flex-1" @click="showFilters = false">
              Apply
              <PhCaretRight class="w-4 h-4 ml-2" />
            </BaseButton>
          </div>
        </div>
      </DrawerContent>
    </Drawer>

    <template #fixed>
      <AppTabbar v-if="authStore.isAuthenticated" active="browse" />
    </template>
  </PageLayout>
</template>
